import { useEffect, useRef, type RefObject } from 'react';

interface RowFocusState {
    editingKey: string | null;
    isSaving: boolean;
    removalKey: string | null;
}

interface Options extends RowFocusState {
    container: RefObject<HTMLElement | null>;
    retryInputId: (rowKey: string) => string;
}

const ROW_ACTIONS_ATTRIBUTE = 'data-row-actions';

const focusRowActionOrContainer = (container: HTMLElement, rowKey: string) => {
    const group = Array.from(container.querySelectorAll<HTMLElement>(`[${ROW_ACTIONS_ATTRIBUTE}]`)).find(
        (element) => element.getAttribute(ROW_ACTIONS_ATTRIBUTE) === rowKey,
    );
    (group?.querySelector<HTMLElement>('button:not([disabled])') ?? container).focus();
};

const focusIsLost = (doc: Document): boolean => !doc.activeElement || doc.activeElement === doc.body;

export function useRestoreRowFocus({ container, editingKey, isSaving, removalKey, retryInputId }: Options): void {
    const previous = useRef<RowFocusState>({ editingKey, isSaving, removalKey });

    useEffect(() => {
        const was = previous.current;
        previous.current = { editingKey, isSaving, removalKey };
        const root = container.current;
        if (!root) return;
        if (was.editingKey !== null && editingKey === null) {
            focusRowActionOrContainer(root, was.editingKey);
        } else if (was.isSaving && !isSaving && editingKey !== null) {
            root.ownerDocument.getElementById(retryInputId(editingKey))?.focus();
        }
        if (was.removalKey !== null && removalKey === null && focusIsLost(root.ownerDocument)) {
            focusRowActionOrContainer(root, was.removalKey);
        }
    }, [container, editingKey, isSaving, removalKey, retryInputId]);
}
